import QRCode from "qrcode";

function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

function splitNums(s) {
  const raw = String(s || "").trim();
  const parts = raw.includes(" ") ? raw.split(/\s+/) : raw.split("");
  return parts.filter(Boolean).slice(0, 3);
}

function drawSpacedDigits(ctx, digits, centerX, y, gapPx) {
  const chars = digits.split("");
  if (!chars.length) return;

  const widths = chars.map((ch) => ctx.measureText(ch).width);
  const totalWidth =
    widths.reduce((a, b) => a + b, 0) + gapPx * (chars.length - 1);

  let x = centerX - totalWidth / 2;
  for (let i = 0; i < chars.length; i++) {
    ctx.fillText(chars[i], x, y);
    x += widths[i] + gapPx;
  }
}

function loadImg(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function makeQrDataUrl(text) {
  const value = String(text || "").trim()
  if (!value) throw new Error("QR text is required for ticket printing")

  return QRCode.toDataURL(value, {
    errorCorrectionLevel: "M",
    margin: 1,
    width: 250,
    color: { dark: "#000000", light: "#FFFFFF" },
  });
}

// --- classification helpers ---
function getBetText(type, nums) {
  const t = String(type || "").toUpperCase();
  const digits = (nums || []).filter(Boolean).slice(0, 3);

  // IMPORTANT: check 3DR first (because it includes "3D")
  if (t.includes("3DR")) {
    const uniqueCount = new Set(digits).size;
    const hasDuplicate = uniqueCount < digits.length; // ex: 1,4,4
    return hasDuplicate ? "Rambolito 3" : "Rambolito 6";
  }

  if (t.includes("3D")) return "Straight";

  return "";
}

function relabelAZ(i) {
  return String.fromCharCode(65 + (i % 26)); // A..Z (wraps after Z)
}

function groupBetsIntoReceipts(bets) {
  const groups = new Map(); // key: betText, value: array of bets

  (bets || []).forEach((b) => {
    const nums = splitNums(b?.numbers || "");
    const betText = getBetText(b?.type, nums);
    if (!betText) return;

    if (!groups.has(betText)) groups.set(betText, []);
    groups.get(betText).push({ ...b });
  });

  // Order exactly like you want
  const order = ["Straight", "Rambolito 3", "Rambolito 6"];

  return order
    .filter((k) => groups.has(k))
    .map((k) => {
      const relabeled = groups.get(k).map((b, idx) => ({
        ...b,
        label: relabelAZ(idx), // reset labels per receipt
      }));
      return { betText: k, bets: relabeled };
    });
}

// --- drawing helpers ---
function drawBetBlock(ctx, bet, xLeft, xRight, y) {
  const type = String(bet?.type || "");
  const label = String(bet?.label || "");
  const price = String(bet?.price || "");
  const nums = splitNums(bet?.numbers || "");

  const betText = getBetText(type, nums);

  // Left side: BET TEXT + relabeled A/B/C
  ctx.fillStyle = "#000";
  ctx.textAlign = "left";
  ctx.font = "900 15.6px Arial";
  ctx.fillText(betText || type, xLeft, y - 2);
  ctx.fillText(label, xLeft, y + 20);

  // Right side numbers
  ctx.textAlign = "right";
  ctx.font = "900 20px Arial";
  const gap = 42;

  const n1 = nums[0] || "";
  const n2 = nums[1] || "";
  const n3 = nums[2] || "";

  ctx.fillText(n3, xRight, y + 4);
  ctx.fillText(n2, xRight - gap, y + 4);
  ctx.fillText(n1, xRight - gap * 2, y + 4);

  // Price below numbers
  ctx.font = "900 15.6px Arial";
  ctx.fillText(`Price: ${price}`, xRight, y + 40);

  return y + 70;
}

async function drawOneReceipt(ctx, yOffset, { meta, bets, assets, paperWidth }) {
  const {
    ticketNumber = "",
    ticketPrice = "",
    betDate = "",
    drawDate = "",
    drawId = "",
    agentId = "",
    qrText = "",
  } = meta;

  const W = paperWidth;

  // Logos
  let logoLeft = null;
  let logoRight = null;
  try {
    if (assets?.logoLeftSrc) logoLeft = await loadImg(assets.logoLeftSrc);
    if (assets?.logoRightSrc) logoRight = await loadImg(assets.logoRightSrc);
  } catch (error) {
    console.warn("Unable to load ticket logo:", error);
  }
  if (logoLeft) ctx.drawImage(logoLeft, 0, yOffset - 5, 100, 100);
  if (logoRight) ctx.drawImage(logoRight, 218, yOffset + 15, 167, 68);

  // QR
  try {
    const qrDataUrl = await makeQrDataUrl(qrText);
    const qrImg = await loadImg(qrDataUrl);
    const qrSize = 178;
    const qrX = W - qrSize - 8;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qrImg, qrX, yOffset + 105, qrSize, qrSize);
    ctx.imageSmoothingEnabled = true;
  } catch (error) {
    console.error("Unable to render ticket QR code:", error);
    throw error;
  }

  // Meta
  let x = 0;
  let y = yOffset + 126;

  const headerFontSize = 17;
  const ticketNumberFontSize = 20;
  const labelFontSize = 17;
  const otherFontSize = 15.6;

  ctx.fillStyle = "#000";
  ctx.textAlign = "left";

  ctx.font = `900 ${headerFontSize}px Arial`;
  ctx.fillText("Bet Date:", x, y);
  y += 22;

  ctx.font = `900 ${labelFontSize}px Arial`;
  ctx.fillText(betDate, x, y);
  y += 30;

  ctx.font = `900 ${headerFontSize}px Arial`;
  ctx.fillText("Draw Date:", x, y);
  y += 23;

  ctx.font = `900 ${labelFontSize}px Arial`;
  ctx.fillText(drawDate, x, y);
  y += 32;

  ctx.font = `900 ${headerFontSize}px Arial`;
  ctx.fillText("Draw ID:", x, y);
  ctx.font = `900 ${labelFontSize}px Arial`;
  ctx.fillText(drawId, x + 65, y);
  y += 32;

  ctx.font = `900 ${headerFontSize}px Arial`;
  ctx.fillText("Agent ID:", x, y);
  ctx.font = `900 ${labelFontSize}px Arial`;
  ctx.fillText(agentId, x + 75, y);
  y += 32;

  ctx.font = `900 ${headerFontSize}px Arial`;
  ctx.fillText("Ticket Price:", x, y);
  ctx.font = `900 ${labelFontSize}px Arial`;
  ctx.fillText(ticketPrice, x + 100, y);
  y += 50;

  // Ticket number (spaced)
  const rawDigits = onlyDigits(ticketNumber);
  ctx.font = `900 ${ticketNumberFontSize}px Arial`;
  ctx.textAlign = "left";
  drawSpacedDigits(ctx, rawDigits, W / 2, y, 10);
  y += 25;

  ctx.textAlign = "center";
  ctx.font = `900 ${otherFontSize}px Arial`;
  ctx.fillText("Ticket Number", W / 2, y);
  y += 19;

  // Signature box
  ctx.textAlign = "left";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 1.7;
  ctx.strokeRect(5, y, W - 10, 50);
  y += 70;

  ctx.textAlign = "center";
  ctx.font = `900 ${otherFontSize}px Arial`;
  ctx.fillText("Signature", W / 2, y);

  // Bets
  ctx.textAlign = "left";
  let by = y + 28;
  const xLeft = 18;
  const xRight = W - 18;

  (bets || []).forEach((bet) => {
    by = drawBetBlock(ctx, bet, xLeft, xRight, by);
  });

  // return bottom y of this receipt
  return by + 10;
}

export async function buildTicketImageDataUrl({
  meta,
  bets,
  assets,
  paperWidth = 384,
}) {
  const W = paperWidth;

  // group into 3 receipts + relabel A/B/C per receipt
  const receipts = groupBetsIntoReceipts(bets);

  // fallback: if nothing matched, print original bets as one receipt
  const finalReceipts =
    receipts.length > 0 ? receipts : [{ betText: "", bets: bets || [] }];

  // compute total height
  const baseH = 560;
  const totalH =
  finalReceipts.reduce(
    (sum, r) => sum + baseH + (r.bets.length || 0) * 95,
    0
  ) + 40;

  const c = document.createElement("canvas");
  c.width = W;
  c.height = totalH;
  const ctx = c.getContext("2d");

  // Background
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, W, totalH);

  // draw each receipt stacked
  let yOffset = 0;

  for (let i = 0; i < finalReceipts.length; i++) {
    const r = finalReceipts[i];

    const bottomY = await drawOneReceipt(ctx, yOffset, {
      meta,
      bets: r.bets,
      assets,
      paperWidth: W,
    });

    yOffset = bottomY;
  }

  return c.toDataURL("image/png");
}